import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, useTheme, IconButton, Collapse, Button } from '@mui/material';
import { lighten, darken } from '@mui/material/styles';
import { LocationOn, AccessTime, Groups, DateRange, ExpandMore } from '@mui/icons-material';
import Parent from '../parentInterface.ts';

interface MobileParentInfoCardProps {
  parent: Parent | null;
  onRefreshChildren?: () => void;
  isRefreshingChildren?: boolean;
  refreshDisabled?: boolean;
  refreshStatusText?: string;
}

const MobileParentInfoCard: React.FC<MobileParentInfoCardProps> = ({
  parent,
  onRefreshChildren,
  isRefreshingChildren = false,
  refreshDisabled = false,
  refreshStatusText = '',
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [expanded, setExpanded] = useState(false);

  if (!parent) {
    return (
      <Card sx={{ width: '100%', mb: 1, borderRadius: '8px' }}>
        <CardContent sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Loading parent information...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatTime = (timeString: string) => {
    return timeString || 'N/A';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.getDate() + ' ' + date.toLocaleString('en-US', { month: 'short' });
  };

  const formatLastChildSync = (value?: string | null): string => {
    if (!value) return 'Never';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString();
  };

  // Combined name with agent
  const displayName = parent.agent_name ? `${parent.name} - ${parent.agent_name}` : parent.name;

  // Theme-aware colors
  const cardBg = isDarkMode
    ? `linear-gradient(145deg, ${darken(theme.palette.background.paper, 0.2)} 0%, ${darken(theme.palette.background.paper, 0.4)} 100%)`
    : `linear-gradient(145deg, ${lighten(theme.palette.background.paper, 0.05)} 0%, ${theme.palette.background.paper} 100%)`;

  const accentGradient = `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`;
  const textPrimaryColor = theme.palette.text.primary;
  const textSecondaryColor = theme.palette.text.secondary;
  const iconColor = theme.palette.mode === 'dark' ? '#9e9e9e' : '#666666';

  return (
    <Card
      sx={{
        width: '100%',
        mb: 1,
        borderRadius: '8px',
        background: cardBg,
        boxShadow: theme.shadows[2],
        position: 'relative',
        minHeight: '40px'
      }}
    >
      {/* Decorative accent strip */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '3px',
        background: accentGradient
      }} />

      <CardContent sx={{ py: 1, px: 1.5 }}>
        {/* Main row with name and ID */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }}>
          {/* Name */}
          <Typography
            variant="subtitle2"
            component="div"
            fontWeight="bold"
            sx={{
              color: textPrimaryColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 'calc(100% - 90px)'
            }}
          >
            {displayName || 'Unnamed Tour'}
          </Typography>

          {/* Right side with ID and expand button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              size="small"
              label={`ID: ${parent.booking_id || 'N/A'}`}
              sx={{
                height: '18px',
                fontSize: '0.6rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.text.secondary,
              }}
            />
            <Chip
              size="small"
              label={`Refreshed: ${formatLastChildSync(parent.last_child_sync_at)}`}
              sx={{
                height: '18px',
                fontSize: '0.6rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.text.secondary,
              }}
            />
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                padding: 0.3,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <ExpandMore fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Compact info row (always visible) */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 0.5,
          flexWrap: 'wrap'
        }}>
          {/* Location */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOn sx={{ fontSize: 14, color: iconColor }} />
            <Typography variant="body2" sx={{
              color: textSecondaryColor,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100px'
            }}>
              {parent.location_name || 'N/A'}
            </Typography>
          </Box>

          {/* Date */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DateRange sx={{ fontSize: 14, color: iconColor }} />
            <Typography variant="body2" sx={{ color: textSecondaryColor, fontSize: '0.75rem' }}>
              {formatDate(parent.date)}
            </Typography>
          </Box>
        </Box>

        {/* Expandable section */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mt: 0.5,
            ml: 0.5,
            flexWrap: 'wrap'
          }}>
            {/* Time */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTime sx={{ mr: 0.3, fontSize: 14, color: iconColor }} />
              <Typography variant="body2" sx={{ color: textSecondaryColor, fontSize: '0.75rem' }}>
                {formatTime(parent.time_from)} - {formatTime(parent.time_to)}
              </Typography>
            </Box>

            {/* Size */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Groups sx={{ mr: 0.3, fontSize: 14, color: iconColor }} />
              <Typography variant="body2" sx={{ color: textSecondaryColor, fontSize: '0.75rem' }}>
                {parent.size || 0}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={onRefreshChildren}
              disabled={isRefreshingChildren || refreshDisabled}
            >
              {isRefreshingChildren ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          {refreshStatusText ? (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {refreshStatusText}
            </Typography>
          ) : null}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MobileParentInfoCard;
